/**
 * Google Calendar Connections Database Operations
 *
 * CRUD operations for managing Google Calendar connection records.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface GoogleCalendarConnection {
  id: string;
  userId: string;
  orgId: string | null;
  googleEmail: string;
  syncEnabled: boolean;
  syncDirection: 'bidirectional' | 'import_only' | 'export_only';
  selectedCalendarIds: string[];
  status: 'active' | 'error' | 'disconnected';
  errorCount: number;
  lastFullSyncAt: string | null;
  createdAt: string;
}

// Supabase row type (snake_case)
interface GoogleCalendarConnectionRow {
  id: string;
  user_id: string;
  org_id: string | null;
  google_email: string;
  sync_enabled: boolean;
  sync_direction: 'bidirectional' | 'import_only' | 'export_only';
  selected_calendar_ids: string[];
  status: 'active' | 'error' | 'disconnected';
  error_count: number;
  last_full_sync_at: string | null;
  created_at: string;
}

function convertConnectionFromSupabase(
  row: GoogleCalendarConnectionRow
): GoogleCalendarConnection {
  return {
    id: row.id,
    userId: row.user_id,
    orgId: row.org_id,
    googleEmail: row.google_email,
    syncEnabled: row.sync_enabled,
    syncDirection: row.sync_direction,
    selectedCalendarIds: row.selected_calendar_ids || [],
    status: row.status,
    errorCount: row.error_count || 0,
    lastFullSyncAt: row.last_full_sync_at,
    createdAt: row.created_at,
  };
}

/**
 * Get all Google Calendar connections for the current user
 */
export async function getConnections(): Promise<GoogleCalendarConnection[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('google_calendar_connections')
      .select('*')
      .order('created_at', { ascending: false })
  );

  if (error) throw error;
  return (data || []).map(convertConnectionFromSupabase);
}

/**
 * Get a single connection by ID
 */
export async function getConnection(
  id: string
): Promise<GoogleCalendarConnection | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('google_calendar_connections')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return convertConnectionFromSupabase(data);
}

/**
 * Delete a connection (tokens are cleaned up by edge function)
 */
export async function deleteConnection(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('google_calendar_connections').delete().eq('id', id)
  );

  if (error) throw error;
}

/**
 * Update a connection's settings
 */
export async function updateConnection(
  id: string,
  updates: Partial<{
    syncEnabled: boolean;
    syncDirection: 'bidirectional' | 'import_only' | 'export_only';
    selectedCalendarIds: string[];
    status: 'active' | 'error' | 'disconnected';
  }>
): Promise<GoogleCalendarConnection> {
  const payload: Record<string, unknown> = {};
  if (updates.syncEnabled !== undefined) payload.sync_enabled = updates.syncEnabled;
  if (updates.syncDirection !== undefined) payload.sync_direction = updates.syncDirection;
  if (updates.selectedCalendarIds !== undefined) payload.selected_calendar_ids = updates.selectedCalendarIds;
  if (updates.status !== undefined) payload.status = updates.status;

  const { data, error } = await withRetry(() =>
    supabase
      .from('google_calendar_connections')
      .update(payload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update connection');
  return convertConnectionFromSupabase(data);
}
